import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Box } from '@mui/material';

import { CasDisplayField, CasErrorDisplay, CasStackRow } from 'src/compass/shared/components';
import CasRefreshButton from 'src/compass/shared/components/CasButton/CasRefreshButton';
import CasLoader from 'src/compass/shared/components/CasLoader/CasLoader';
import useRetrieve from 'src/compass/shared/hooks/reactQuery/useRetrieve';
import { DateFormat, formatDate } from 'src/compass/shared/util/casMoment';
import { useFeatureFlags } from 'src/compass/application';
import { ClaimStatusFilter_NotDoneOrAttached } from '../shared/ClaimListing/ClaimListing';
import ReviewedMultiSceClaimsDialog from '../shared/ClaimDetails/ReviewedMultiSceClaimsDialog';
import ClaimDueRemindersDialog from './ClaimDueRemindersDialog';

export default function DashboardTab() {
  const queryResult = useRetrieve({ url: 'Sce/Claims/Dashboard' });
  const navigate = useNavigate();
  const [showDueReminders, setShowDueReminders] = useState(false);
  const [showReviewedMultiSce, setShowReviewedMultiSce] = useState(false);
  // Hidden until the Show Claim Due Reminders feature flag is on (Administration > Configuration >
  // Feature Flags), so reminders can stay off in Prod until they are ready for it.
  const featureFlags = useFeatureFlags();

  const { data, isFetching, isError, refetch } = queryResult;

  if (isFetching) return <CasLoader />;

  if (isError) return <CasErrorDisplay message="Error retrieving data from the server." />;

  const {
    noticeMissingCount,
    reviewUploadsCount,
    sendCount,
    docusignReviewCount,
    multiSceReadyToAttachCount,
    callbackCount,
    daysLeftCount,
    numberOfDaysLeft,
    dueRemindersLastSent,
    dueRemindersSentToday,
  } = data;

  const baseUrl = '/endorsements/claimtracking';

  // A zero count shows blank. A non-breaking space rather than '' keeps the row its full height,
  // so the rows stay evenly spaced.
  const BLANK = ' ';
  const showCount = (count?: number | null) => count || BLANK;

  return (
    <CasStackRow alignItems="center">
      {/*
        A grid rather than stacked rows, so every count lines up in one column. Each
        CasDisplayField is a flex row of label / value / (empty) second label; display: contents
        hands those three to the grid as its three columns.
      */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, max-content)',
          columnGap: '10px',
          alignItems: 'baseline',
          '& .cas-display-field': { display: 'contents' },
        }}
      >
        <CasDisplayField
          horizontal
          label="Notice Missing"
          value={showCount(noticeMissingCount)}
          onClick={() => navigate(`${baseUrl}/entry/noticeMissing`)}
        />
        <CasDisplayField
          horizontal
          label="Review Uploads"
          value={showCount(reviewUploadsCount)}
          onClick={() => navigate(`${baseUrl}/entry/fwdContracts/review`)}
        />
        <CasDisplayField
          horizontal
          label="Send"
          value={showCount(sendCount)}
          onClick={() => navigate(`${baseUrl}/entry/send`)}
        />
        <CasDisplayField
          horizontal
          label="Docusign Review"
          value={showCount(docusignReviewCount)}
          onClick={() => navigate(`${baseUrl}/entry/docusignReview`)}
        />
        <CasDisplayField
          horizontal
          label="Multi-SCE Claims ready to Attach"
          value={showCount(multiSceReadyToAttachCount)}
          onClick={() => setShowReviewedMultiSce(true)}
        />
        <CasDisplayField
          horizontal
          label="Callback"
          value={showCount(callbackCount)}
          onClick={() => navigate(`${baseUrl}/entry/callback`)}
        />
        <CasDisplayField
          horizontal
          label={`${numberOfDaysLeft} days or less left to sign`}
          value={showCount(daysLeftCount)}
          onClick={() =>
            navigate(
              `${baseUrl}/search?${new URLSearchParams({
                daysLeftLessThan: numberOfDaysLeft,
                status: ClaimStatusFilter_NotDoneOrAttached,
              })}`
            )
          }
        />
        <CasDisplayField
          horizontal
          label="Rejected Claims"
          value={BLANK}
          onClick={() =>
            navigate(
              `${baseUrl}/search?${new URLSearchParams({
                rejected: 'true',
              })}`
            )
          }
        />
        {featureFlags.showClaimDueReminders && (
          <CasDisplayField
            horizontal
            label="Claim due reminders"
            value={
              dueRemindersSentToday ? (
                `Sent ${formatDate(dueRemindersLastSent, DateFormat.MDY)}`
              ) : (
                <Box
                  component="span"
                  sx={{ bgcolor: 'warning.lighter', px: 0.75, py: 0.25, borderRadius: 0.5 }}
                >
                  Not sent today
                </Box>
              )
            }
            onClick={() => setShowDueReminders(true)}
          />
        )}

        <Box sx={{ gridColumn: '1 / -1' }}>
          <CasRefreshButton refetch={refetch} />
        </Box>
      </Box>

      {showDueReminders && (
        <ClaimDueRemindersDialog
          sentToday={dueRemindersSentToday}
          onClose={() => {
            setShowDueReminders(false);
            refetch();
          }}
        />
      )}

      {showReviewedMultiSce && (
        <ReviewedMultiSceClaimsDialog
          onClose={() => {
            setShowReviewedMultiSce(false);
            refetch();
          }}
        />
      )}
    </CasStackRow>
  );
}
